import { useMemo } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'

import {
  routes,
  useGetWorkspaces,
  useRegisterCommandBarCommands,
  useUserAuthorizations,
} from '@sketch/modules-common'
import { useToast } from '@sketch/toasts'
import {
  GetUserNotificationsCountDocument,
  useAcceptShareMembershipInvitationMutation,
  useGetNotificationQuery,
  useGetUserNotificationsCountQuery,
  useNotificationMarkAllAsReadMutation,
  useSetNotificationSubscriptionMutation,
  useUpdateNotificationMutation,
} from '@sketch/gql-types'
import { useEventDispatch } from '@sketch/utils'

import { notificationMarkAllAsReadOptimisticResponse } from '../../../graphql'
import { getNotificationPath } from '../../../components/InboxPreview/getNotificationPath'
import { useSetNotificationState } from '../../../components/InboxPreview/useSetNotificationState'
import { recordPinned, recordUnpinned } from '../pinOrder'
import { useInboxSearchContext } from '../InboxSearchContext'
import { createInboxCommands, InboxFilterKey } from './createInboxCommands'
import { createNotificationCommands } from './createNotificationCommands'

// `userMarkedAllNotificationsAsRead` is augmented onto the events map in
// cloud-frontend (UpdatesOld). Re-declare the matching shape here so
// `@sketch/user`'s TS project sees it — declaration merging reconciles them.
declare module '@sketch/utils' {
  export interface EventsMap {
    userMarkedAllNotificationsAsRead: void
  }
}

/**
 * Registers command-bar entries for the inbox. Mounted inside the `Inbox`
 * view, which is only rendered when the `better-notifications` flag is on — so
 * these commands are implicitly gated by that flag and only present while the
 * user is on the inbox.
 *
 * Two registrations:
 * - `inbox-list`: list-level actions (sort, filters, mark all as read).
 * - `inbox-notification`: actions for the currently-open notification,
 *   mirroring `InboxPreviewActions`. Empty when no notification is selected.
 */
export const InboxCommandBarRegistration = () => {
  const history = useHistory()
  const { showToast } = useToast()
  const {
    sort,
    setSort,
    options,
    setOptions,
    resetAll,
    isOptionsEmpty,
    search,
  } = useInboxSearchContext()
  // The unread-count query runs app-wide (navbar/sidebar) and now also carries
  // the most recent unread notification, so both the unread count and the
  // "Go to Most Recent Unread" target come from cache with no extra request.
  const { data: countData } = useGetUserNotificationsCountQuery({
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  })
  const unreadCount =
    countData?.me.unreadNotificationsCount?.meta.totalCount ?? 0
  const mostRecentUnreadId =
    countData?.me.unreadNotificationsCount?.entries[0]?.identifier ?? null

  const itemMatch = useRouteMatch<{ notificationId: string }>({
    path: routes.UPDATES_ITEM.template(),
    exact: true,
  })
  const activeNotificationId = itemMatch?.params?.notificationId ?? null

  // Reuses the preview's query (same operation + variables), so this is a cache
  // hit while a notification is open and skipped otherwise.
  const { data } = useGetNotificationQuery({
    errorPolicy: 'all',
    skip: !activeNotificationId,
    variables: { identifier: activeNotificationId ?? '' },
  })
  const notification = activeNotificationId
    ? (data?.notification ?? null)
    : null

  // --- List-level mutations -------------------------------------------------

  const dispatchMarkedAllAsRead = useEventDispatch(
    'userMarkedAllNotificationsAsRead'
  )
  const [markAllAsRead] = useNotificationMarkAllAsReadMutation({
    redirectErrors: true,
    onError: 'do-nothing',
    optimisticResponse: {
      __typename: 'RootMutationType',
      ...notificationMarkAllAsReadOptimisticResponse,
    },
    UNSAFE_ignoreResults: true,
    // Refetch both the unread badge and the open notifications list — the new
    // inbox (unlike the legacy list) doesn't react to the event below, so the
    // cached entries need refetching to drop their unread dots.
    refetchQueries: [
      { query: GetUserNotificationsCountDocument },
      'getNotifications',
    ],
    awaitRefetchQueries: true,
    onCompleted: () => dispatchMarkedAllAsRead(),
  })

  // --- Notification-level mutations ----------------------------------------

  const setNotificationState = useSetNotificationState()
  const [updateNotification] = useUpdateNotificationMutation({
    onError: 'show-toast',
    redirectErrors: true,
    onCompleted: data => {
      const nowPinned = data.updateNotification?.notification?.isPinned
      if (!notification) return
      if (nowPinned) {
        recordPinned(notification.identifier)
      } else {
        recordUnpinned(notification.identifier)
      }
      showToast(nowPinned ? 'Pinned' : 'Unpinned')
    },
  })
  const [setSubscription] = useSetNotificationSubscriptionMutation({
    onError: 'show-toast',
    onCompleted: data =>
      showToast(
        data.setNotificationSubscription.share?.subscriptionStatus === 'ON'
          ? 'Following'
          : 'Unfollowed'
      ),
  })
  const [acceptInvitation] = useAcceptShareMembershipInvitationMutation({
    onError: 'show-toast',
    onCompleted: () => showToast('You now have access to this document'),
  })

  // --- Derived notification capabilities (mirrors `resolveActions`) ---------

  const typename = notification?.__typename
  const rawPath = notification ? getNotificationPath(notification) : null

  let openDocumentPath: string | null = null
  let goToWorkspaceId: string | null = null
  let openInvitePath: string | null = null

  if (notification) {
    switch (typename) {
      case 'WorkspaceInvitedNotification':
        if (notification.membership?.acceptedAt && notification.workspace) {
          goToWorkspaceId = notification.workspace.identifier
        } else if (rawPath) {
          openInvitePath = rawPath
        }
        break
      case 'WorkspaceInviteAcceptedNotification':
        if (notification.workspace) {
          goToWorkspaceId = notification.workspace.identifier
        }
        break
      // An archived folder has no document to open.
      case 'FolderArchivedNotification':
        break
      default:
        openDocumentPath = rawPath
    }
  }

  // Member-specific fields are only accessible after narrowing on `__typename`
  // — these vary per notification type.
  let annotationIdentifier: string | undefined
  let isSubscribed = false
  let acceptShareIdentifier: string | null = null
  let assetPath: string | null = null

  if (notification) {
    if (
      notification.__typename === 'CommentNotification' ||
      notification.__typename === 'MentionNotification'
    ) {
      annotationIdentifier = notification.comment?.annotation.identifier
      isSubscribed = notification.share?.subscriptionStatus === 'ON'
    } else if (notification.__typename === 'AddShareMemberNotification') {
      acceptShareIdentifier = notification.share
        ?.userHasUnacceptedInvitationToView
        ? notification.share.identifier
        : null
    } else if (
      notification.__typename === 'DownloadableAssetRenderedNotification'
    ) {
      // Only expose the download action when the asset is ready, mirroring
      // `InboxListItemActions`/`DownloadableAssetPreview` — otherwise the
      // command could navigate to a not-yet-available URL.
      const asset =
        notification.notifiedVersion?.document?.downloadableAssets[0]
      assetPath =
        asset?.status === 'AVAILABLE' && asset.path ? asset.path : null
    }
  }

  const canToggleSubscription = !!annotationIdentifier

  // --- List-level registration ----------------------------------------------

  const hasActiveFilters = !isOptionsEmpty || !!search

  // Workspaces the user can filter by — same accessibility check the inbox
  // filter sidebar uses.
  const { workspaces } = useGetWorkspaces()
  const { hasAccessToWorkspace } = useUserAuthorizations()
  const accessibleWorkspaces = useMemo(
    () =>
      (workspaces ?? []).filter(workspace =>
        hasAccessToWorkspace({
          identifier: workspace.identifier,
          customer: workspace.customer,
          userRole: workspace.userRole,
        })
      ),
    [workspaces, hasAccessToWorkspace]
  )

  useRegisterCommandBarCommands(
    'inbox-list',
    () =>
      createInboxCommands({
        sort,
        options,
        hasActiveFilters,
        unreadCount,
        mostRecentUnreadId,
        workspaces: accessibleWorkspaces,
        selectedWorkspaceId: options.workspace,
        onMarkAllAsRead: () => {
          if (unreadCount === 0) return
          markAllAsRead()
        },
        onGoToRecentUnread: () => {
          if (mostRecentUnreadId) {
            history.push(
              routes.UPDATES_ITEM.create({
                notificationId: mostRecentUnreadId,
              })
            )
          }
        },
        onSetSort: setSort,
        onToggleFilter: (key: InboxFilterKey) =>
          setOptions(current => ({ ...current, [key]: !current[key] })),
        onSelectWorkspace: (workspaceId: string | null) =>
          setOptions(current => ({ ...current, workspace: workspaceId })),
        onClearFilters: resetAll,
      }),
    [
      sort,
      options,
      hasActiveFilters,
      unreadCount,
      mostRecentUnreadId,
      accessibleWorkspaces,
    ]
  )

  // --- Notification-level registration --------------------------------------

  useRegisterCommandBarCommands(
    'inbox-notification',
    () => {
      if (!notification) return []

      return createNotificationCommands({
        notification,
        openDocumentPath,
        goToWorkspaceId,
        openInvitePath,
        canToggleSubscription,
        isSubscribed,
        canAccept: !!acceptShareIdentifier,
        assetPath,
        onToggleReadState: () =>
          setNotificationState(
            notification.identifier,
            notification.__typename,
            notification.state === 'UNREAD' ? 'READ' : 'UNREAD'
          ),
        onTogglePin: () =>
          updateNotification({
            variables: {
              identifier: notification.identifier,
              input: { pin: !notification.isPinned },
            },
          }),
        // Open in a new tab so the inbox stays put, matching `SeeDocumentAction`.
        onOpenDocument: () => {
          if (openDocumentPath) window.open(openDocumentPath, '_blank')
        },
        onGoToWorkspace: () => {
          if (goToWorkspaceId) {
            history.push(
              routes.WORKSPACE.create({ workspaceId: goToWorkspaceId })
            )
          }
        },
        onOpenInvite: () => {
          if (openInvitePath) history.push(openInvitePath)
        },
        onToggleSubscription: () =>
          setSubscription({
            variables: {
              state: isSubscribed ? 'OFF' : 'ON',
              annotationIdentifier,
            },
          }),
        onAccept: () => {
          if (acceptShareIdentifier) {
            acceptInvitation({
              variables: { shareIdentifier: acceptShareIdentifier },
            })
          }
        },
        onDownload: () => {
          if (!assetPath) return
          window.open(assetPath, '_self')
          setNotificationState(
            notification.identifier,
            notification.__typename,
            'READ'
          )
        },
        onClose: () => history.push(routes.UPDATES.create({})),
      })
    },
    [
      notification?.identifier,
      notification?.state,
      notification?.isPinned,
      notification?.__typename,
      openDocumentPath,
      goToWorkspaceId,
      openInvitePath,
      canToggleSubscription,
      isSubscribed,
      acceptShareIdentifier,
      assetPath,
    ]
  )

  return null
}
